import { useState, useEffect, useCallback, useMemo } from "react";
import AdminLayout from "@/components/AdminLayout";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Search, Pencil, GraduationCap, Users, BookOpen, AlertTriangle, ArrowRight, Trash2, CheckCircle, XCircle, Clock, LogIn, Camera } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function timeAgo(dateStr) {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

export default function AdminStudents() {
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [groups, setGroups] = useState([]);
  const [modules, setModules] = useState([]);
  const [search, setSearch] = useState("");
  const [filterGroup, setFilterGroup] = useState("all");
  const [filterCourse, setFilterCourse] = useState("all");
  const [filterEnrollment, setFilterEnrollment] = useState("all");
  const [editStudent, setEditStudent] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ course_id: "", group_id: "", override_module_id: "", course_status_override: "", address: "" });
  const [selected, setSelected] = useState([]);
  const [bulkGroup, setBulkGroup] = useState("");

  const load = useCallback(async () => {
    const [s, c, g, m] = await Promise.all([
      api.get("/students", { params: { search: search || undefined, group_id: filterGroup === "all" ? undefined : filterGroup, course_id: filterCourse === "all" ? undefined : filterCourse } }),
      api.get("/courses"), api.get("/groups"), api.get("/modules")
    ]);
    setStudents(s.data);
    setCourses(c.data);
    setGroups(g.data);
    setModules(m.data);
  }, [search, filterGroup, filterCourse]);

  useEffect(() => { load(); }, [load]);

  const openEdit = (s) => {
    setEditStudent(s);
    setForm({
      course_id: s.course_id || "none",
      group_id: s.group_id || "none",
      override_module_id: s.override_module_id || "none",
      course_status_override: s.course_status_override || "none",
      address: s.address || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const updates = {};
      updates.course_id = form.course_id === "none" ? null : form.course_id;
      updates.group_id = form.group_id === "none" ? null : form.group_id;
      updates.override_module_id = form.override_module_id === "none" ? null : form.override_module_id;
      updates.course_status_override = form.course_status_override === "none" ? null : form.course_status_override;
      updates.address = form.address || null;
      await api.put(`/students/${editStudent.id}`, updates);
      toast.success("Student updated");
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleSelect = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const handleBulkMove = async () => {
    if (!bulkGroup || selected.length === 0) return;
    try {
      for (const sid of selected) {
        await api.put(`/students/${sid}`, { group_id: bulkGroup === "none" ? null : bulkGroup });
      }
      toast.success(`Moved ${selected.length} students`);
      setSelected([]);
      setBulkGroup("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const getGroupName = (id) => groups.find((g) => g.id === id)?.name || "Unassigned";
  const getCourseName = (id) => courses.find((c) => c.id === id)?.title || "Not enrolled";
  const getModuleName = (id) => modules.find((m) => m.id === id)?.title || "";

  const getCurrentModule = (s) => {
    if (s.override_module_id) return { name: getModuleName(s.override_module_id), isOverride: true };
    if (s.group_id) {
      const group = groups.find((g) => g.id === s.group_id);
      if (group?.current_module_id) return { name: getModuleName(group.current_module_id), isOverride: false };
    }
    return { name: "None", isOverride: false };
  };

  const handleDelete = async (student) => {
    if (!window.confirm(`Delete ${student.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/students/${student.id}`);
      toast.success(`${student.name} deleted`);
      setSelected((s) => s.filter((id) => id !== student.id));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleApprove = async (student) => {
    try {
      await api.post(`/students/${student.id}/approve`);
      toast.success(`${student.name} enrolled`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleReject = async (student) => {
    if (!window.confirm(`Reject ${student.name}'s enrollment?`)) return;
    try {
      await api.post(`/students/${student.id}/reject`);
      toast.success(`${student.name} rejected`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const filteredGroupsForForm = useMemo(() => {
    return form.course_id && form.course_id !== "none" ? groups.filter((g) => g.course_id === form.course_id) : groups;
  }, [groups, form.course_id]);

  const filteredModules = useMemo(() => {
    return form.course_id && form.course_id !== "none" ? modules.filter((m) => m.course_id === form.course_id) : modules;
  }, [modules, form.course_id]);

  // Client-side enrollment filter
  const displayStudents = filterEnrollment === "all"
    ? students
    : students.filter((s) => (s.enrollment_status || "accepted") === filterEnrollment);

  const pendingCount = students.filter((s) => (s.enrollment_status || "accepted") === "pending").length;
  const selectAll = () => setSelected(displayStudents.length === selected.length ? [] : displayStudents.map((s) => s.id));

  return (
    <AdminLayout>
      <div data-testid="admin-students" className="space-y-6 animate-fade-in-up">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-[#5C3A2E] font-semibold tracking-tight">Students</h1>
          <p className="text-[#2B2B2B]/50 mt-1 text-sm">
            {students.length} total{pendingCount > 0 && <span className="text-[#C6A969] font-medium"> · {pendingCount} pending enrollment</span>}
          </p>
        </div>

        {/* Enrollment Status Filter Tabs */}
        <div className="flex gap-2 flex-wrap">
          {[
            { value: "all", label: "All Students" },
            { value: "pending", label: `Pending (${pendingCount})` },
            { value: "accepted", label: "Enrolled" },
            { value: "rejected", label: "Rejected" },
          ].map((f) => (
            <button
              key={f.value}
              data-testid={`filter-enrollment-${f.value}`}
              onClick={() => setFilterEnrollment(f.value)}
              className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
                filterEnrollment === f.value
                  ? "bg-[#5C3A2E] text-white"
                  : "bg-[#EADBC8]/30 text-[#2B2B2B]/60 hover:bg-[#EADBC8]/50"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#2B2B2B]/30" />
            <Input
              data-testid="student-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search students..."
              className="pl-10 rounded-xl border-[#EADBC8]"
            />
          </div>
          <Select value={filterGroup} onValueChange={setFilterGroup}>
            <SelectTrigger data-testid="filter-group-select" className="w-full sm:w-48 rounded-xl border-[#EADBC8]">
              <SelectValue placeholder="All Groups" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Groups</SelectItem>
              {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Bulk Actions */}
        {selected.length > 0 && (
          <div data-testid="bulk-actions" className="bg-[#5C3A2E] rounded-xl p-4 flex items-center gap-4 text-white">
            <span className="text-sm font-medium">{selected.length} selected</span>
            <Select value={bulkGroup} onValueChange={setBulkGroup}>
              <SelectTrigger data-testid="bulk-group-select" className="w-48 rounded-xl border-white/20 bg-white/10 text-white">
                <SelectValue placeholder="Move to group..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Remove from group</SelectItem>
                {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button data-testid="bulk-move-button" onClick={handleBulkMove} disabled={!bulkGroup} size="sm" className="bg-[#C6A969] text-[#5C3A2E] rounded-full hover:bg-[#C6A969]/90">
              <ArrowRight className="w-4 h-4 mr-1" /> Move
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setSelected([])} className="text-white/60 hover:text-white hover:bg-white/10 rounded-full ml-auto">
              Clear
            </Button>
          </div>
        )}

        {/* Student List */}
        <div className="grid gap-3">
          {displayStudents.length > 0 && (
            <label className="flex items-center gap-2 px-2 text-xs text-[#2B2B2B]/40 cursor-pointer">
              <input type="checkbox" checked={selected.length === displayStudents.length && displayStudents.length > 0} onChange={selectAll} className="rounded border-[#EADBC8]" />
              Select all
            </label>
          )}
          {displayStudents.map((s) => {
            const currentMod = getCurrentModule(s);
            const status = s.enrollment_status || "accepted";
            const isPending = status === "pending";
            const isRejected = status === "rejected";
            const lastLogin = timeAgo(s.last_login_at);
            return (
              <div key={s.id} data-testid={`student-card-${s.id}`} className={`bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border p-4 sm:p-5 studio-card ${isPending ? "border-[#C6A969]/50" : isRejected ? "border-red-200 opacity-60" : "border-[#EADBC8]/30"}`}>
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <input type="checkbox" checked={selected.includes(s.id)} onChange={() => toggleSelect(s.id)} className="rounded border-[#EADBC8] flex-shrink-0" />
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden ${isPending ? "bg-[#C6A969]/20" : "bg-[#EADBC8]"}`}>
                      {s.avatar_url ? (
                        <img src={`${API_BASE}/api/files/${s.avatar_url}`} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-sm font-semibold text-[#5C3A2E]">{s.name?.[0]?.toUpperCase() || "?"}</span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-[#2B2B2B] text-sm truncate">{s.name}</h4>
                        {isPending && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#C6A969]/20 text-[#5C3A2E]"><Clock className="w-2.5 h-2.5" /> Pending Enrollment</span>}
                        {isRejected && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-100 text-red-700">Rejected</span>}
                      </div>
                      <p className="text-xs text-[#2B2B2B]/40 truncate">{s.email}</p>
                      {/* Last login indicator */}
                      <div className="flex items-center gap-1 mt-0.5">
                        <LogIn className="w-2.5 h-2.5 text-[#2B2B2B]/30" />
                        <span className={`text-[10px] ${lastLogin ? "text-[#2B2B2B]/40" : "text-[#C6A969]"}`}>
                          {lastLogin ? `Last login: ${lastLogin}` : "Never logged in"}
                        </span>
                      </div>
                    </div>
                    <div className="hidden sm:flex items-center gap-4 text-xs text-[#2B2B2B]/50">
                      <div className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        <span>{s.group_id ? getGroupName(s.group_id) : "No group"}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <BookOpen className="w-3 h-3" />
                        <span className="max-w-[120px] truncate">{currentMod.name || "No module"}</span>
                        {currentMod.isOverride && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#C6A969]/20 text-[#5C3A2E]">Override</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {isPending && (
                      <>
                        <Button data-testid={`approve-student-${s.id}`} variant="ghost" size="sm" onClick={() => handleApprove(s)} title="Enrol Student" className="text-green-600 hover:bg-green-50 rounded-full h-8 px-3 gap-1 text-xs">
                          <CheckCircle className="w-4 h-4" /> Enrol
                        </Button>
                        <Button data-testid={`reject-student-${s.id}`} variant="ghost" size="sm" onClick={() => handleReject(s)} title="Reject" className="text-red-400 hover:bg-red-50 rounded-full h-8 w-8 p-0">
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    <Button data-testid={`edit-student-${s.id}`} variant="ghost" size="sm" onClick={() => openEdit(s)} className="text-[#5C3A2E] hover:bg-[#EADBC8]/30 rounded-full h-8 w-8 p-0">
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button data-testid={`delete-student-${s.id}`} variant="ghost" size="sm" onClick={() => handleDelete(s)} className="text-red-400 hover:bg-red-50 rounded-full h-8 w-8 p-0">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
          {displayStudents.length === 0 && (
            <div className="text-center py-12">
              <GraduationCap className="w-10 h-10 text-[#EADBC8] mx-auto mb-3" />
              <p className="text-[#2B2B2B]/40 text-sm">
                {filterEnrollment === "all" ? "No students found" : `No ${filterEnrollment} students`}
              </p>
            </div>
          )}
        </div>

        {/* Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-white rounded-xl max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#5C3A2E]">Edit Student</DialogTitle>
              <DialogDescription className="text-[#2B2B2B]/50 text-sm">{editStudent?.name} · {editStudent?.email}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              {/* Avatar Upload */}
              <div className="flex items-center gap-4">
                <div className="relative w-16 h-16 rounded-full overflow-hidden bg-[#EADBC8] flex items-center justify-center flex-shrink-0">
                  {editStudent?.avatar_url ? (
                    <img src={`${API_BASE}/api/files/${editStudent.avatar_url}`} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-xl font-semibold text-[#5C3A2E]">{editStudent?.name?.[0]?.toUpperCase() || "?"}</span>
                  )}
                  <label className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/30 transition-colors cursor-pointer group">
                    <Camera className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    <input data-testid="student-avatar-upload" type="file" accept="image/*" onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const formData = new FormData();
                      formData.append("file", file);
                      try {
                        const { data } = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
                        await api.put(`/students/${editStudent.id}`, { avatar_url: data.storage_path });
                        toast.success("Photo updated");
                        load();
                        setEditStudent({ ...editStudent, avatar_url: data.storage_path });
                      } catch { toast.error("Upload failed"); }
                    }} className="hidden" />
                  </label>
                </div>
                <div>
                  <p className="text-sm font-medium text-[#2B2B2B]">{editStudent?.name}</p>
                  <p className="text-xs text-[#2B2B2B]/40">Click photo to change</p>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Course</label>
                <Select value={form.course_id} onValueChange={(v) => setForm({ ...form, course_id: v, group_id: "none" })}>
                  <SelectTrigger data-testid="student-course-select" className="rounded-xl border-[#EADBC8]">
                    <SelectValue placeholder="Select course" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not Enrolled</SelectItem>
                    {courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Group</label>
                <Select value={form.group_id} onValueChange={(v) => setForm({ ...form, group_id: v })}>
                  <SelectTrigger data-testid="student-group-select" className="rounded-xl border-[#EADBC8]">
                    <SelectValue placeholder="Select group" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Group</SelectItem>
                    {filteredGroupsForForm.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 flex items-center gap-1.5">
                  Override Module
                  <AlertTriangle className="w-3 h-3 text-[#C6A969]" />
                </label>
                <Select value={form.override_module_id} onValueChange={(v) => setForm({ ...form, override_module_id: v })}>
                  <SelectTrigger data-testid="student-override-select" className="rounded-xl border-[#EADBC8]">
                    <SelectValue placeholder="No override" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Override (use group module)</SelectItem>
                    {filteredModules.map((m) => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Course Status Override</label>
                <Select value={form.course_status_override} onValueChange={(v) => setForm({ ...form, course_status_override: v })}>
                  <SelectTrigger data-testid="student-status-select" className="rounded-xl border-[#EADBC8]">
                    <SelectValue placeholder="Use group default" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Use Group Default</SelectItem>
                    <SelectItem value="starting_soon">Starting Soon</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Address (for invoicing)</label>
                <textarea data-testid="student-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} placeholder="Student's address" className="w-full rounded-xl border border-[#EADBC8] bg-white px-4 py-2.5 text-sm text-[#2B2B2B] focus:ring-2 focus:ring-[#C6A969] focus:border-transparent resize-none" />
              </div>
              <Button data-testid="save-student-button" onClick={handleSave} className="w-full bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90 h-11">
                Save Changes
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
